import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ComponentRef,
} from "react";
import { Drawer as VaulDrawer } from "vaul";

import { cn, flattenStyles, platformProps } from "../../lib";
import { composeRefs } from "../slot";
import { isContentSized, resolveSnapPoints } from "./snap-points";
import type {
  BottomSheetDescriptionPrimitiveProps,
  BottomSheetRootPrimitiveProps,
  BottomSheetSnapPoint,
  BottomSheetTitlePrimitiveProps,
} from "./types";
import { fromVaulSnapPoint, toVaulActiveSnapPoint, toVaulSnapPoints } from "./vaul-snap";

/**
 * iOS Safari already pans the visual viewport to keep a focused input visible.
 * Vaul's `repositionInputs` then lifts the drawer by the keyboard height on top
 * of that, pushing the sheet off screen (vaul#619). On iOS we let Safari handle
 * it alone. iPadOS reports a Mac UA, so touch support disambiguates it.
 */
const isIOSWeb =
  typeof navigator !== "undefined" &&
  (/iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes("Macintosh") && navigator.maxTouchPoints > 1));

/**
 * The sheet's surface (web), Vaul's Content with focus handled. Vaul stops
 * Radix's open autofocus and nothing focused the sheet, so focus stayed on the
 * page behind it; and Radix returns focus on close only to its own Trigger,
 * which sheets don't have, so it fell to the page. Now the sheet itself takes
 * focus on open (not its first field, which would raise a phone's keyboard;
 * Tab moves on into it), and focus goes back to the element that had it when
 * the sheet opened. The opener is recorded on the first render, before a child
 * can take focus (an autofocusing field also makes Radix skip
 * `onOpenAutoFocus`), as Dialog's web Content does.
 */
const SheetContent = forwardRef<
  ComponentRef<typeof VaulDrawer.Content>,
  ComponentPropsWithoutRef<typeof VaulDrawer.Content>
>((props, ref) => {
  const opener = useRef<Element | null | undefined>(undefined);
  if (opener.current === undefined) opener.current = document.activeElement;
  const contentRef = useRef<ComponentRef<typeof VaulDrawer.Content> | null>(null);
  const composedRef = useMemo(() => composeRefs(ref, contentRef), [ref]);
  return (
    <VaulDrawer.Content
      ref={composedRef}
      {...props}
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        contentRef.current?.focus({ preventScroll: true });
      }}
      onCloseAutoFocus={(event) => {
        const target = opener.current;
        if (target instanceof HTMLElement && target !== document.body && target.isConnected) {
          event.preventDefault();
          target.focus({ preventScroll: true });
        }
      }}
    />
  );
});

SheetContent.displayName = "SheetContent";

/**
 * BottomSheet Root - Web implementation (Vaul)
 *
 * Renders the entire portaled drawer envelope (Root + Portal + Overlay + Content)
 * so consumers can pass arbitrary children that end up inside the sheet surface.
 */
/** `window.innerHeight`, re-read on resize: Vaul's snap points are fractions of it. */
function useViewportHeight() {
  const [height, setHeight] = useState(() =>
    typeof window === "undefined" ? 0 : window.innerHeight,
  );
  useEffect(() => {
    const update = () => setHeight(window.innerHeight);
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return height;
}

const BottomSheetRoot = forwardRef<HTMLDivElement, BottomSheetRootPrimitiveProps>(
  (
    {
      open,
      onOpenChange,
      snapPoints,
      activeSnapPoint,
      setActiveSnapPoint,
      dismissible = true,
      overlay,
      contentClassName,
      contentStyle,
      overlayClassName,
      className,
      children,
      testID,
    },
    ref,
  ) => {
    // One snap-point format on both platforms, translated to Vaul's here. The
    // translation depends on the viewport height (the surface is the viewport
    // minus the peek), so it follows a resize.
    const points = useMemo(() => resolveSnapPoints(snapPoints), [snapPoints]);
    const viewportHeight = useViewportHeight();
    const vaulSnapPoints = useMemo(
      () => toVaulSnapPoints(points, viewportHeight),
      [points, viewportHeight],
    );
    const contentSized = isContentSized(points);

    // Vaul is always given the active detent: it picks its own uncontrolled
    // one only once, so a list that appears later would have none and sit off
    // screen. An uncontrolled caller's detent is tracked here from Vaul's
    // reports (drags). As on native: the requested detent if listed, else the
    // first.
    const [ownSnapPoint, setOwnSnapPoint] = useState<BottomSheetSnapPoint | null>(null);
    const requested = activeSnapPoint !== undefined ? activeSnapPoint : ownSnapPoint;
    const listed = typeof requested === "number" && (points as number[]).includes(requested);
    const vaulActiveSnapPoint = toVaulActiveSnapPoint(
      points,
      contentSized ? "content" : listed ? requested : points[0],
      viewportHeight,
    );

    // Vaul positions snap points with inline styles on the drawer and overlay,
    // and leaves them behind when the list becomes content-sized; clear them
    // there (they're Vaul's, not React's) rather than remount the content
    const drawerRef = useRef<HTMLDivElement | null>(null);
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const composedRef = useMemo(() => composeRefs(ref, drawerRef), [ref]);
    const wasContentSized = useRef(contentSized);
    useLayoutEffect(() => {
      if (contentSized && !wasContentSized.current) {
        drawerRef.current?.style.removeProperty("transform");
        drawerRef.current?.style.removeProperty("transition");
        overlayRef.current?.style.removeProperty("opacity");
      }
      wasContentSized.current = contentSized;
    }, [contentSized]);

    // Vaul reports back its own (scaled) snap value; map it to the caller's
    const handleSnapChange = useCallback(
      (vaulSnap: number | string | null) => {
        const snap = fromVaulSnapPoint(points, vaulSnap, viewportHeight);
        setOwnSnapPoint(snap);
        setActiveSnapPoint?.(snap);
      },
      [setActiveSnapPoint, points, viewportHeight],
    );

    const overlayNode =
      overlay === null ? null : overlay !== undefined ? (
        overlay
      ) : (
        <VaulDrawer.Overlay
          ref={overlayRef}
          className={cn("bg-luce-overlay-20 fixed inset-0 z-40", overlayClassName)}
        />
      );

    return (
      <VaulDrawer.Root
        open={open}
        onOpenChange={onOpenChange}
        snapPoints={vaulSnapPoints}
        activeSnapPoint={vaulActiveSnapPoint}
        setActiveSnapPoint={handleSnapChange}
        dismissible={dismissible}
        repositionInputs={!isIOSWeb}
      >
        <VaulDrawer.Portal>
          {overlayNode}
          <SheetContent
            ref={composedRef}
            className={cn(
              // The viewport minus the top safe-area inset and the peek, as on native
              "fixed inset-x-0 bottom-0 z-50 flex max-h-[calc(100dvh-env(safe-area-inset-top,0px)-theme(spacing.spacing-3xl))] flex-col outline-none",
              // Snap points need a fixed-height surface to translate against;
              // content mode hugs its content instead.
              vaulSnapPoints ? "h-full" : undefined,
              contentClassName,
              className,
            )}
            // Only when given: Vaul positions the surface with its own inline style
            {...(contentStyle !== undefined && { style: flattenStyles([contentStyle]) })}
            {...platformProps({ testID, slot: "bottom-sheet" })}
          >
            {children}
          </SheetContent>
        </VaulDrawer.Portal>
      </VaulDrawer.Root>
    );
  },
);

BottomSheetRoot.displayName = "BottomSheetRoot";

/**
 * BottomSheet Title - Web (Vaul)
 *
 * Provides accessible labeling for the sheet. Use `asChild` to merge into a
 * Typography element from the molecule layer.
 */
const BottomSheetTitle = forwardRef<
  ComponentRef<typeof VaulDrawer.Title>,
  BottomSheetTitlePrimitiveProps
>(({ asChild, className, children }, ref) => (
  <VaulDrawer.Title ref={ref} asChild={asChild} className={className}>
    {children}
  </VaulDrawer.Title>
));

BottomSheetTitle.displayName = "BottomSheetTitle";

/**
 * BottomSheet Description - Web (Vaul)
 */
const BottomSheetDescription = forwardRef<
  ComponentRef<typeof VaulDrawer.Description>,
  BottomSheetDescriptionPrimitiveProps
>(({ asChild, className, children }, ref) => (
  <VaulDrawer.Description ref={ref} asChild={asChild} className={className}>
    {children}
  </VaulDrawer.Description>
));

BottomSheetDescription.displayName = "BottomSheetDescription";

export { BottomSheetRoot, BottomSheetTitle, BottomSheetDescription };
